import React, {FC, Key, PropsWithChildren, ReactNode, useCallback, useMemo} from 'react';
import styled from 'styled-components';

import {DataEventTrackingProvider} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingProvider';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Layer} from '../../../../../../libs/shared/ui/src/components/layers/layer';
import {SafeAreaProvider} from '../../../../../../libs/shared/ui/src/components/safeArea/safeAreaProvider';
import {shadows} from '../../../../../../libs/shared/ui/src/styles/colorStyles';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {AppState} from '../../../../../store/appState';
import {getCompanyId} from '../../../../../store/data/company/companySelectors';
import {getSynopsisSize} from '../../../../../store/data/localPreferences/localPreferencesSelectors';
import {getUserId} from '../../../../../store/data/teammates/teammatesSelectors';
import {makeGetIsPanelPopup} from '../../../../../store/panels/panelsSelectors';
import {toggleSynopsisAction} from '../../../../../store/panels/viewer/viewerPanelActions';
import {
  getViewerPanelId,
  makeGetViewerPanelPage,
} from '../../../../../store/panels/viewer/viewerPanelSelectors';
import {ViewerPanelPageTypesEnum} from '../../../../../store/panels/viewer/viewerPanelState';
import {topBarHeight} from '../../../../app/topBar/topBarHelpers';
import {ErrorFallback} from '../../../../errors/errorFallback';
import {ErrorReporterBase} from '../../../../errors/errorReporterBase';
import {SynopsisHeader} from './synopsisHeader';
import {SynopsisViewerContainer} from './synopsisViewerContainer';

/*
 * Constants.
 */

const SYNOPSIS_MARGIN = 8;

/*
 * Props.
 */

interface SynopsisProps {
  panelId: ViewerPanelId;
}

/*
 * Styles.
 */

const StyledSideBarAside = styled.aside<{$width: number}>`
  position: absolute;
  display: flex;
  flex-direction: column;

  top: ${topBarHeight + SYNOPSIS_MARGIN}px;
  left: ${SYNOPSIS_MARGIN}px;
  bottom: ${SYNOPSIS_MARGIN}px;
  width: ${($p) => `${$p.$width - SYNOPSIS_MARGIN - 4}px`};

  border-radius: 20px;
  background-color: ${(p) => p.theme.appBackground};
  ${shadows.synopsis};
`;

const StyledModalDiv = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  inset: 0;
  background-color: ${(p) => p.theme.appBackground};
`;

/*
 * Component.
 */

export const SynopsisContainer: FC<SynopsisProps> = ({panelId}) => {
  const getViewerPanelPage = useMemo(
    () => (state: AppState) => makeGetViewerPanelPage(getViewerPanelId)(state, {panelId}),
    [panelId],
  );
  const page = useAppSelector(getViewerPanelPage);

  const getIsInPopup = useMemo(
    () => (state: AppState) => makeGetIsPanelPopup(getViewerPanelId)(state, {panelId}),
    [panelId],
  );
  const isInPopup = useAppSelector(getIsInPopup);

  if (page.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION || !page.isSynopsisOpen) {
    return null;
  }

  const {conversationId} = page;

  return (
    <DataEventTrackingProvider
      context={{
        name: 'p3',
        payload: {conversation_id: page.conversationId, section: 'synopsis'},
      }}
    >
      <Layer>
        <SafeAreaProvider>
          <ModalOrSidebar isInPopup={isInPopup}>
            <SynopsisErrorReporter errorKey={page.uid} isTopLevel={false} panelId={panelId}>
              <SynopsisViewerContainer
                panelId={panelId}
                conversationId={conversationId}
                isInPopup={isInPopup}
                filter={page.synopsisFilter}
              />
            </SynopsisErrorReporter>
          </ModalOrSidebar>
        </SafeAreaProvider>
      </Layer>
    </DataEventTrackingProvider>
  );
};

const ModalOrSidebar: FC<PropsWithChildren<{isInPopup: boolean}>> = ({isInPopup, children}) => {
  const sideBarWidth = useAppSelector(getSynopsisSize);

  return isInPopup ? (
    <StyledModalDiv>{children}</StyledModalDiv>
  ) : (
    <StyledSideBarAside $width={sideBarWidth}>{children}</StyledSideBarAside>
  );
};

interface SynopsisErrorReporterProps {
  errorKey?: Key;
  isTopLevel: boolean;
  panelId: ViewerPanelId;
  children: ReactNode;
}

const SynopsisErrorReporter: FC<SynopsisErrorReporterProps> = ({errorKey, isTopLevel, panelId, children}) => {
  const dispatch = useAppDispatch();

  const companyId = useAppSelector(getCompanyId);
  const userId = useAppSelector(getUserId);

  // If the synopsis crashes, let the user close the panel, but don't worry about data events.
  const onRequestClose = useCallback(() => {
    dispatch(toggleSynopsisAction(panelId, false));
  }, [dispatch, panelId]);

  return (
    <ErrorReporterBase
      errorKey={errorKey}
      renderFallback={(fallbackProps) => (
        <>
          <SynopsisHeader onRequestClose={onRequestClose} />
          <ErrorFallback
            companyId={companyId}
            userId={userId}
            error={fallbackProps.error}
            onRetryClick={fallbackProps.onRetry}
            isTopLevel={isTopLevel}
          />
        </>
      )}
    >
      {children}
    </ErrorReporterBase>
  );
};
